import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { ArrowUp, Instagram, Facebook, Twitter, Linkedin, Youtube, Globe } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
}

export const FooterSection: React.FC<SectionProps> = ({ section, siteData, onNavigate }) => {
  const content = section.content || {};
  const style = section.style || {};

  const title = content.title || siteData.name || 'Adiosoft';
  const description =
    content.description ||
    'Tüm hakları saklıdır. Uluslararası kurumsal ve turizm standartlarına tam uyumludur.';

  const bgColor = style.backgroundColor || '#020617';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || (isDark ? '#94A3B8' : '#64748B');
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const anchorId = style.anchorId || style.customId || 'footer';

  // Social Links
  const socialLinks = (siteData.settings as any)?.social || (siteData.settings as any)?.socialLinks || content.social || {};

  return (
    <footer
      id={anchorId}
      data-section-id={section.id}
      data-section-type="FOOTER"
      className="py-16 px-4 sm:px-6 lg:px-8 border-t select-none transition-colors"
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      }}
    >
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Col */}
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-white text-sm shadow-sm"
                style={{ backgroundColor: accentColor }}
              >
                {title.charAt(0)}
              </div>
              <span className={`font-black text-xl tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {title}
              </span>
            </div>
            <p className={`text-xs sm:text-sm max-w-md leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {description}
            </p>

            {/* Social Icons */}
            {Object.keys(socialLinks).length > 0 && (
              <div className="flex items-center gap-2.5 pt-2">
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className={`p-2 rounded-xl border transition ${
                      isDark
                        ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white hover:border-slate-700'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:text-black hover:border-slate-300'
                    }`}
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {socialLinks.facebook && (
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noreferrer"
                    className={`p-2 rounded-xl border transition ${
                      isDark
                        ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white hover:border-slate-700'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:text-black hover:border-slate-300'
                    }`}
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {socialLinks.twitter && (
                  <a
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noreferrer"
                    className={`p-2 rounded-xl border transition ${
                      isDark
                        ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white hover:border-slate-700'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:text-black hover:border-slate-300'
                    }`}
                  >
                    <Twitter className="w-4 h-4" />
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className={`p-2 rounded-xl border transition ${
                      isDark
                        ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white hover:border-slate-700'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:text-black hover:border-slate-300'
                    }`}
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="space-y-3">
            <div className={`text-xs font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Hızlı Sayfalar
            </div>
            <div className="space-y-2 text-xs">
              {siteData.pages?.map((p) => (
                <div
                  key={p.id}
                  onClick={() => onNavigate && onNavigate(p.slug)}
                  className={`cursor-pointer transition ${
                    isDark ? 'hover:text-white' : 'hover:text-slate-900'
                  }`}
                >
                  {p.title}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Col */}
          <div className="space-y-3">
            <div className={`text-xs font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-slate-900'}`}>
              İletişim
            </div>
            <div className={`space-y-1.5 text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {siteData.settings?.phone && <div>{siteData.settings.phone}</div>}
              {siteData.settings?.email && <div>{siteData.settings.email}</div>}
              {siteData.settings?.address && <div>{siteData.settings.address}</div>}
              {!siteData.settings?.phone && !siteData.settings?.email && !siteData.settings?.address && (
                <div className="text-slate-500 italic">İletişim bilgisi eklenmedi</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4 text-xs"
          style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}
        >
          <div>
            © {new Date().getFullYear()} {title}. Tüm Hakları Saklıdır.
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={scrollToTop}
              className={`p-2 rounded-xl transition ${
                isDark
                  ? 'bg-white/5 hover:bg-white/10 text-white'
                  : 'bg-slate-200 hover:bg-slate-300 text-slate-800'
              }`}
              title="Yukarı Çık"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
